<template>
  <WdsInputTime
    v-if="!timesheetFormStore.isViewOnly"
    v-model="timesheetFormStore.tableDateEntries[props.col.dateString][props.row.entriesKey]"
    :disabled="timesheetFormStore.isDateDisabled(props.col.dateString)"
    class="tw-w-full"
    :error="getErrorMessage(props.col.dateString, props.row.entriesKey)"
    :data-test="'time-input-' + props.col.dateString + '-' + props.row.entriesKey"
    @blur="inputTouched($event, props.col.dateString, props.row.entriesKey)"
    @update:model-value="syncStartAndEndTimes($event, props.col.dateString, props.row.entriesKey)"
  />
  <div v-else class="tw-text-right tw-p-sm">
    {{
      useTwelveHourTime
        ? convertToTwelveHourTime(
            timesheetFormStore.tableDateEntries[props.col.dateString][props.row.entriesKey],
          )
        : timesheetFormStore.tableDateEntries[props.col.dateString][props.row.entriesKey]
    }}
  </div>
</template>

<script setup lang="ts">
import { WdsInputTime } from '@worksome/wds-components'
import moment from 'moment/moment'
import convertToTwelveHourTime from '@/components/Timesheet/utilities/convertToTwelveHourTime'
import { useTimesheetFormStore } from '@/components/Timesheet/store/useTimesheetFormStore'
import { useValidationStore } from '@/components/Timesheet/store/useValidationStore'
import useTwelveHourTimeFormat from '@/composables/useTwelveHourTimeFormat'

interface Props {
  row: any
  col: any
}

const props = defineProps<Props>()

const timesheetFormStore = useTimesheetFormStore()
const timesheetValidationStore = useValidationStore()
const useTwelveHourTime = useTwelveHourTimeFormat(navigator.language)

const getErrorMessage = (columnDate, entryKey) =>
  timesheetValidationStore.v$[columnDate]?.[entryKey]?.$errors[0]?.$message

const inputTouched = async (e, dateKey, fieldKey) => {
  // Our time input only updates the model when the format is correct
  // Here we handle when blurring the input before actually writing the am/pm
  const currentValue = e.target.value?.trim()?.toLowerCase() || ''
  const is12HourFormat = currentValue.slice(-2) === 'am' || currentValue.slice(-2) === 'pm'
  if (is12HourFormat) {
    return
  }
  timesheetFormStore.tableDateEntries[dateKey][fieldKey] = currentValue
}

const syncStartAndEndTimes = (val: string, date: string, timeLabel: 'startTime' | 'endTime') => {
  if (timeLabel === 'startTime') {
    if (
      (timesheetFormStore.tableDateEntries[date].endTime === '' ||
        moment(timesheetFormStore.tableDateEntries[date].endTime, 'HH:mm').isBefore(
          moment(val, 'HH:mm'),
        )) &&
      moment(val, 'HH:mm').isBefore(moment('23:00', 'HH:mm'))
    ) {
      timesheetFormStore.tableDateEntries[date].endTime = moment(val, 'HH:mm')
        .add(1, 'hour')
        .format('HH:mm')
    }
  }
  if (timeLabel === 'endTime') {
    if (
      (timesheetFormStore.tableDateEntries[date].startTime === '' ||
        moment(timesheetFormStore.tableDateEntries[date].startTime, 'HH:mm').isAfter(
          moment(val, 'HH:mm'),
        )) &&
      moment(val, 'HH:mm').isAfter(moment('01:00', 'HH:mm'))
    ) {
      timesheetFormStore.tableDateEntries[date].startTime = moment(val, 'HH:mm')
        .subtract(1, 'hour')
        .format('HH:mm')
    }
  }
}
</script>
